import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Head, Section, ArticleCTA } from '@/components';
import { ARTICLES } from '@/data/constants';
import { ArrowLeft, Clock, Calendar, User } from 'lucide-react';

/**
 * Single Article Page.
 * Renders a full blog post with high readability and SEO metadata.
 */
const Article: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = ARTICLES.find(a => a.slug === slug);

  if (!article) {
    return <Navigate to="/insights" replace />;
  }

  return (
    <>
      <Head 
        title={`${article.title} | Kivosystems Insights`}
        description={article.excerpt}
        canonicalUrl={`https://www.kivosystems.com/insights/${article.slug}`}
        url={`https://www.kivosystems.com/insights/${article.slug}`}
        image={article.image}
        type="article"
        keywords={article.tags.join(', ')}
      />

      {/* Hero */}
      <div className="relative min-h-[60vh] flex items-end pb-16 pt-32 bg-obsidian overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-full h-full bg-linear-to-b from-sapphire-900/40 via-obsidian to-obsidian" />
            <img src={article.image} alt="" className="w-full h-full object-cover grayscale" />
        </div>

        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/insights" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-sapphire-500 mb-8 hover:text-white transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Back to Intel
            </Link>
            
            <h1 className="text-4xl md:text-6xl font-display font-medium text-white mb-8 leading-tight">
                {article.title}
            </h1>

            <div className="flex flex-wrap gap-6 text-sm text-gray-400 border-t border-white/10 pt-8">
                <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-sapphire-500" />
                    <span className="text-white font-bold">{article.author}</span>
                    <span className="opacity-50">({article.role})</span>
                </div>
                <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-sapphire-500" />
                    <span>{article.date}</span>
                </div>
                <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-sapphire-500" />
                    <span>{article.readTime}</span>
                </div>
            </div>
        </div>
      </div>

      {/* Content */}
      <Section className="bg-obsidian pt-0">
        <div className="max-w-2xl mx-auto">
            <div 
                className="prose prose-invert prose-lg prose-sapphire max-w-none 
                prose-headings:font-display prose-headings:font-medium prose-p:text-gray-300 prose-p:leading-relaxed
                prose-blockquote:border-l-sapphire-500 prose-blockquote:bg-white/5 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
                prose-img:rounded-2xl prose-img:border prose-img:border-white/10"
                dangerouslySetInnerHTML={{ __html: article.content }} 
            />

            {/* Tags */}
            <div className="mt-16 pt-8 border-t border-white/10">
                <div className="flex flex-wrap gap-2">
                    {article.tags.map(tag => (
                        <span key={tag} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs font-mono text-gray-400">
                            #{tag}
                        </span>
                    ))}
                </div>
            </div>
        </div>
      </Section>

      <ArticleCTA />
    </>
  );
};

export default Article;
